import type {SubnetSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/Subnet';
import type {EventsNetworkSopNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/EventsNetwork';
import type {PerspectiveCameraObjNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/PerspectiveCamera';
import type {GeoObjNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/Geo';
import type {RootManagerNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/manager/Root';
import type {ExampleData, DataCallbackOptions} from '../../Common';
import type {ReturnedNodeDataDict} from '../../ExampleUtils';

export function createData(options: DataCallbackOptions): ExampleData {
	const {scene} = options;
	const root = scene.root();
	const scene_root_nodes: ReturnedNodeDataDict = {};
	function create_geo1(parentNode: RootManagerNode) {
		var geo1 = parentNode.createNode('geo');
		geo1.setName('geo1');
		const geo1_nodes: ReturnedNodeDataDict = {};
		function create_attribCreate1(geo1: GeoObjNode) {
			var attribCreate1 = geo1.createNode('attribCreate');
			attribCreate1.setName('attribCreate1');
			const attribCreate1_nodes: ReturnedNodeDataDict = {};
			attribCreate1.uiData.setComment(
				'This adds an up attribute. This is used to define the orientation of the duplicated objects, along side the normal attribute.\nIt is currently bypassed, so you would need to un-bypass it to see how it changes the output of the copy node'
			);
			attribCreate1.uiData.setPosition(-200, -50);
			attribCreate1.flags.bypass.set(true);
			attribCreate1.p.name.set('up');
			attribCreate1.p.size.set(3);
			attribCreate1.p.value1.set('10000 * rand(@ptnum * 124.543)');
			attribCreate1.p.value3.set([0.1, 1, -1]);
			attribCreate1.params.postCreateSpareParams();
			attribCreate1.params.runOnSceneLoadHooks();
			return {node: attribCreate1, children: attribCreate1_nodes};
		}
		function create_attribCreate2(geo1: GeoObjNode) {
			var attribCreate2 = geo1.createNode('attribCreate');
			attribCreate2.setName('attribCreate2');
			const attribCreate2_nodes: ReturnedNodeDataDict = {};
			attribCreate2.uiData.setComment(
				'This node adds a pscale attribute. Using an expression, the pscale is set to 0 for the first point and 1 for the last point.\nThe node is currently bypassed. so you would need to un-bypass it to see its result.'
			);
			attribCreate2.uiData.setPosition(-200, 250);
			attribCreate2.flags.bypass.set(true);
			attribCreate2.p.name.set('pscale');
			attribCreate2.p.value1.set('@ptnum / (pointsCount(0)-1)');
			attribCreate2.params.postCreateSpareParams();
			attribCreate2.params.runOnSceneLoadHooks();
			return {node: attribCreate2, children: attribCreate2_nodes};
		}
		function create_circle1(geo1: GeoObjNode) {
			var circle1 = geo1.createNode('circle');
			circle1.setName('circle1');
			const circle1_nodes: ReturnedNodeDataDict = {};
			circle1.uiData.setComment(
				'this circle node creates points that will then be used by the copy node below to define the position of the new objects. Try changing the arcAngle and segments to see how this changes the output of the copy node'
			);
			circle1.uiData.setPosition(-200, -550);
			circle1.p.radius.set(3);
			circle1.p.segments.set(6);
			circle1.p.connectLastPoint.set(false);
			circle1.p.arcAngle.set(90);
			circle1.params.postCreateSpareParams();
			circle1.params.runOnSceneLoadHooks();
			return {node: circle1, children: circle1_nodes};
		}
		function create_copy1(geo1: GeoObjNode) {
			var copy1 = geo1.createNode('copy');
			copy1.setName('copy1');
			const copy1_nodes: ReturnedNodeDataDict = {};
			copy1.uiData.setComment(
				'This copy nodes duplicate the content of the left input onto the points of the second input.\nIt uses the point position, but also the following attributes: scale (3 float), pscale (1 float), normal (3 float) and up (3 float) to transform the created geometry.'
			);
			copy1.uiData.setPosition(-450, 450);
			copy1.flags.display.set(true);
			copy1.params.postCreateSpareParams();
			copy1.params.runOnSceneLoadHooks();
			return {node: copy1, children: copy1_nodes};
		}
		function create_normals1(geo1: GeoObjNode) {
			var normals1 = geo1.createNode('normals');
			normals1.setName('normals1');
			const normals1_nodes: ReturnedNodeDataDict = {};
			normals1.uiData.setComment(
				'this adds a normal attribute. Using expressions, it sets the normals to the same value as the position. But you can set it to any value.\nYou can also try and bypass it to see the result.'
			);
			normals1.uiData.setPosition(-200, -250);
			normals1.p.edit.set(true);
			normals1.p.updateX.set(true);
			normals1.p.x.set('@P.x');
			normals1.p.updateY.set(true);
			normals1.p.y.set('@P.y');
			normals1.p.updateZ.set(true);
			normals1.p.z.set('@P.z');
			normals1.params.postCreateSpareParams();
			normals1.params.runOnSceneLoadHooks();
			return {node: normals1, children: normals1_nodes};
		}
		function create_normals2(geo1: GeoObjNode) {
			var normals2 = geo1.createNode('normals');
			normals2.setName('normals2');
			const normals2_nodes: ReturnedNodeDataDict = {};
			normals2.uiData.setComment(
				'this normal node sets the normals to [0,1,0] to all points. Try and replace the other normal node by this one to see how that changes the output of the copy node'
			);
			normals2.uiData.setPosition(150, -250);
			normals2.p.edit.set(true);
			normals2.p.updateX.set(true);
			normals2.p.x.set(0);
			normals2.p.updateY.set(true);
			normals2.p.y.set(1);
			normals2.p.updateZ.set(true);
			normals2.p.z.set(0);
			normals2.p.recompute.set(false);
			normals2.params.postCreateSpareParams();
			normals2.params.runOnSceneLoadHooks();
			return {node: normals2, children: normals2_nodes};
		}
		function create_subnet_arrow(geo1: GeoObjNode) {
			var subnet_arrow = geo1.createNode('subnet');
			subnet_arrow.setName('subnet_arrow');
			const subnet_arrow_nodes: ReturnedNodeDataDict = {};
			function create_box1(subnet_arrow: SubnetSopNode) {
				var box1 = subnet_arrow.createNode('box');
				box1.setName('box1');
				const box1_nodes: ReturnedNodeDataDict = {};
				box1.uiData.setPosition(-750, 0);
				box1.params.postCreateSpareParams();
				box1.params.runOnSceneLoadHooks();
				return {node: box1, children: box1_nodes};
			}
			function create_merge1(subnet_arrow: SubnetSopNode) {
				var merge1 = subnet_arrow.createNode('merge');
				merge1.setName('merge1');
				const merge1_nodes: ReturnedNodeDataDict = {};
				merge1.uiData.setPosition(-700, 300);
				merge1.p.compact.set(true);
				merge1.params.postCreateSpareParams();
				merge1.params.runOnSceneLoadHooks();
				return {node: merge1, children: merge1_nodes};
			}
			function create_subnetOutput1(subnet_arrow: SubnetSopNode) {
				var subnetOutput1 = subnet_arrow.createNode('subnetOutput');
				subnetOutput1.setName('subnetOutput1');
				const subnetOutput1_nodes: ReturnedNodeDataDict = {};
				subnetOutput1.uiData.setPosition(-700, 700);
				subnetOutput1.params.postCreateSpareParams();
				subnetOutput1.params.runOnSceneLoadHooks();
				return {node: subnetOutput1, children: subnetOutput1_nodes};
			}
			function create_transform1(subnet_arrow: SubnetSopNode) {
				var transform1 = subnet_arrow.createNode('transform');
				transform1.setName('transform1');
				const transform1_nodes: ReturnedNodeDataDict = {};
				transform1.uiData.setPosition(-700, 400);
				transform1.flags.display.set(true);
				transform1.p.s.set([1, 0.30000000000000004, 1]);
				transform1.p.scale.set(0.43);
				transform1.params.postCreateSpareParams();
				transform1.params.runOnSceneLoadHooks();
				return {node: transform1, children: transform1_nodes};
			}
			function create_tube1(subnet_arrow: SubnetSopNode) {
				var tube1 = subnet_arrow.createNode('tube');
				tube1.setName('tube1');
				const tube1_nodes: ReturnedNodeDataDict = {};
				tube1.uiData.setPosition(-550, 150);
				tube1.p.segmentsRadial.set(3);
				tube1.p.center.set([0, 0, 1]);
				tube1.p.direction.set([0, 1, 0]);
				tube1.params.postCreateSpareParams();
				tube1.params.runOnSceneLoadHooks();
				return {node: tube1, children: tube1_nodes};
			}
			subnet_arrow_nodes['box1'] = create_box1(subnet_arrow);
			subnet_arrow_nodes['merge1'] = create_merge1(subnet_arrow);
			subnet_arrow_nodes['subnetOutput1'] = create_subnetOutput1(subnet_arrow);
			subnet_arrow_nodes['transform1'] = create_transform1(subnet_arrow);
			subnet_arrow_nodes['tube1'] = create_tube1(subnet_arrow);
			subnet_arrow_nodes['merge1'].node.setInput(0, subnet_arrow_nodes['box1'].node);
			subnet_arrow_nodes['merge1'].node.setInput(1, subnet_arrow_nodes['tube1'].node);
			subnet_arrow_nodes['subnetOutput1'].node.setInput(0, subnet_arrow_nodes['transform1'].node);
			subnet_arrow_nodes['transform1'].node.setInput(0, subnet_arrow_nodes['merge1'].node);
			subnet_arrow.uiData.setPosition(-650, 50);
			subnet_arrow.params.postCreateSpareParams();
			subnet_arrow.params.runOnSceneLoadHooks();
			return {node: subnet_arrow, children: subnet_arrow_nodes};
		}
		geo1_nodes['attribCreate1'] = create_attribCreate1(geo1);
		geo1_nodes['attribCreate2'] = create_attribCreate2(geo1);
		geo1_nodes['circle1'] = create_circle1(geo1);
		geo1_nodes['copy1'] = create_copy1(geo1);
		geo1_nodes['normals1'] = create_normals1(geo1);
		geo1_nodes['normals2'] = create_normals2(geo1);
		geo1_nodes['subnet_arrow'] = create_subnet_arrow(geo1);
		geo1_nodes['attribCreate1'].node.setInput(0, geo1_nodes['normals1'].node);
		geo1_nodes['attribCreate2'].node.setInput(0, geo1_nodes['attribCreate1'].node);
		geo1_nodes['copy1'].node.setInput(0, geo1_nodes['subnet_arrow'].node);
		geo1_nodes['copy1'].node.setInput(1, geo1_nodes['attribCreate2'].node);
		geo1_nodes['normals1'].node.setInput(0, geo1_nodes['circle1'].node);
		geo1_nodes['normals2'].node.setInput(0, geo1_nodes['circle1'].node);
		geo1.uiData.setPosition(-50, -150);
		geo1.flags.display.set(true);
		geo1.params.postCreateSpareParams();
		geo1.params.runOnSceneLoadHooks();
		return {node: geo1, children: geo1_nodes};
	}
	function create_grid(parentNode: RootManagerNode) {
		var grid = parentNode.createNode('geo');
		grid.setName('grid');
		const grid_nodes: ReturnedNodeDataDict = {};
		function create_planeHelper1(grid: GeoObjNode) {
			var planeHelper1 = grid.createNode('planeHelper');
			planeHelper1.setName('planeHelper1');
			const planeHelper1_nodes: ReturnedNodeDataDict = {};
			planeHelper1.uiData.setPosition(0, 0);
			planeHelper1.flags.display.set(true);
			planeHelper1.params.postCreateSpareParams();
			planeHelper1.params.runOnSceneLoadHooks();
			return {node: planeHelper1, children: planeHelper1_nodes};
		}
		grid_nodes['planeHelper1'] = create_planeHelper1(grid);
		grid.uiData.setPosition(-50, -250);
		grid.flags.display.set(true);
		grid.params.postCreateSpareParams();
		grid.params.runOnSceneLoadHooks();
		return {node: grid, children: grid_nodes};
	}
	function create_hemisphereLight1(parentNode: RootManagerNode) {
		var hemisphereLight1 = parentNode.createNode('hemisphereLight');
		hemisphereLight1.setName('hemisphereLight1');
		const hemisphereLight1_nodes: ReturnedNodeDataDict = {};
		hemisphereLight1.uiData.setPosition(150, 100);
		hemisphereLight1.flags.display.set(true);
		hemisphereLight1.params.postCreateSpareParams();
		hemisphereLight1.params.runOnSceneLoadHooks();
		return {node: hemisphereLight1, children: hemisphereLight1_nodes};
	}
	function create_perspectiveCamera1(parentNode: RootManagerNode) {
		var perspectiveCamera1 = parentNode.createNode('perspectiveCamera');
		perspectiveCamera1.setName('perspectiveCamera1');
		const perspectiveCamera1_nodes: ReturnedNodeDataDict = {};
		function create_events1(perspectiveCamera1: PerspectiveCameraObjNode) {
			var events1 = perspectiveCamera1.createNode('eventsNetwork');
			events1.setName('events1');
			const events1_nodes: ReturnedNodeDataDict = {};
			function create_cameraOrbitControls1(events1: EventsNetworkSopNode) {
				var cameraOrbitControls1 = events1.createNode('cameraOrbitControls');
				cameraOrbitControls1.setName('cameraOrbitControls1');
				const cameraOrbitControls1_nodes: ReturnedNodeDataDict = {};
				cameraOrbitControls1.uiData.setPosition(150, 50);
				cameraOrbitControls1.p.target.set([0.6610931372932695, -1.5878491680619118, -1.544847541286043]);
				cameraOrbitControls1.params.postCreateSpareParams();
				cameraOrbitControls1.params.runOnSceneLoadHooks();
				return {node: cameraOrbitControls1, children: cameraOrbitControls1_nodes};
			}
			events1_nodes['cameraOrbitControls1'] = create_cameraOrbitControls1(events1);
			events1.uiData.setPosition(-200, 50);
			events1.params.postCreateSpareParams();
			events1.params.runOnSceneLoadHooks();
			return {node: events1, children: events1_nodes};
		}
		perspectiveCamera1_nodes['events1'] = create_events1(perspectiveCamera1);
		perspectiveCamera1.uiData.setPosition(-200, 100);
		perspectiveCamera1.flags.display.set(true);
		perspectiveCamera1.p.t.set([4.0859463258358675, 5.868268087915243, 1.3055653371223168]);
		perspectiveCamera1.p.r.set([-68.72444324394021, 22.97761684435261, 45.07194773819147]);
		perspectiveCamera1.p.s.set([1, 0.9999999999999998, 1]);
		perspectiveCamera1.p.controls.set('./events1/cameraOrbitControls1');
		perspectiveCamera1.params.postCreateSpareParams();
		perspectiveCamera1.params.runOnSceneLoadHooks();
		return {node: perspectiveCamera1, children: perspectiveCamera1_nodes};
	}
	scene_root_nodes['geo1'] = create_geo1(root);
	scene_root_nodes['grid'] = create_grid(root);
	scene_root_nodes['hemisphereLight1'] = create_hemisphereLight1(root);
	scene_root_nodes['perspectiveCamera1'] = create_perspectiveCamera1(root);
	return {
		scene,
		cameraPath: scene_root_nodes['perspectiveCamera1'].node.path(),
		focusedNodes: Object.values(scene_root_nodes['geo1'].children).map((n) => n.node),
		autoLayout: false,
	};
}
// export const sopCopy_withTemplateAttributes = [
// 	new Example({
// 		name: `${ExamplePrefix.BY_NODE}/sop/copy/withTemplateAttributes`,
// 		createData,
// 		categories: [ExampleCategory.INSTANCING],
// 		relatedNodes: [CopySopNode],
// 	}),
// 	// new Example({name: `${ExamplePrefix.BY_CATEGORY}/instancing/CopyWithTemplateAttributes`, createData}),
// ];
